<script lang="ts">
  import { untrack } from 'svelte';
  import { store } from './store.svelte.ts';
  import { router } from './router.svelte.ts';
  import type { Cart } from '@chariot/contract';

  /**
   * Registering carts, optionally already standing somewhere.
   *
   * Creating a cart and then having to walk a sweep to say where it is makes the catalogue
   * wrong for as long as nobody walks one — so the site is part of registering it.
   */

  interface Props {
    /** Fixed when opened from a site; otherwise the operator picks, or leaves it empty. */
    site_code?: string | null;
    onclose: () => void;
  }
  let { site_code = null, onclose }: Props = $props();

  let name = $state('Chariot');
  let count = $state(10);
  // A seed, not a mirror: the sheet is mounted fresh per use.
  let chosenSite = $state(untrack(() => site_code) ?? '');
  let busy = $state(false);
  let error = $state<string | null>(null);
  let created = $state<Cart[] | null>(null);
  const range = $derived(
    created && created.length > 0
      ? { first: created[0]!.code, last: created[created.length - 1]!.code, n: created.length }
      : null,
  );

  const activeSites = $derived(store.sites.filter((s) => s.status === 'active'));
  const siteName = $derived(chosenSite ? store.siteName(chosenSite) : null);

  async function create() {
    busy = true;
    error = null;
    try {
      const prefix = name.trim() || 'Chariot';
      created =
        count > 1
          ? await store.createCartsBulk(count, prefix, chosenSite || null)
          : [await store.createCart(prefix, chosenSite || null)];
    } catch (e) {
      error = e instanceof Error ? e.message : String(e);
    } finally {
      busy = false;
    }
  }
</script>

<div class="overlay" role="dialog" aria-modal="true" aria-label="Ajouter des chariots">
  <div class="sheet">
    {#if range}
      <h2>{range.n} chariot{range.n === 1 ? '' : 's'} créé{range.n === 1 ? '' : 's'}</h2>
      <p class="small muted">
        <span class="mono">{range.first}</span>
        {#if range.n > 1}→ <span class="mono">{range.last}</span>{/if}
        {#if siteName}· enregistrés à {siteName}{/if}
      </p>
      <div class="banner info">
        Les étiquettes restent à imprimer et à coller : un chariot sans étiquette ne peut pas
        être scanné.
      </div>
      <div class="row wrap">
        <button class="primary grow" onclick={() => { created = null; onclose(); }}>Terminé</button>
        <button class="ghost" onclick={() => { created = null; onclose(); router.go('labels'); }}>
          Imprimer les étiquettes
        </button>
      </div>
    {:else}
      <h2>Ajouter des chariots</h2>
      <label class="field">
        <span>Nom (ou préfixe si plusieurs)</span>
        <input bind:value={name} placeholder="Chariot" />
      </label>
      <label class="field">
        <span>Combien</span>
        <input type="number" min="1" max="500" bind:value={count} />
      </label>

      {#if site_code}
        <p class="small muted">
          Enregistrés comme présents à <strong>{siteName}</strong>.
        </p>
      {:else}
        <label class="field">
          <span>Où sont-ils ?</span>
          <select bind:value={chosenSite}>
            <option value="">— nulle part pour l'instant —</option>
            {#each activeSites as s (s.code)}<option value={s.code}>{s.name}</option>{/each}
          </select>
        </label>
      {/if}

      <p class="tiny muted">
        {#if chosenSite}
          Ils comptent dès maintenant comme présents à {siteName}, sans attendre une tournée.
          Aucun autre chariot n'est déclaré manquant pour autant.
        {:else}
          Sans site, ils resteront « jamais localisés » jusqu'à leur premier scan.
        {/if}
        Les codes sont attribués par le serveur, en séquence : création en ligne uniquement.
      </p>

      {#if error}<div class="banner danger">{error}</div>{/if}
      <div class="row">
        <button class="primary grow" onclick={create} disabled={busy}>
          {#if busy}<span class="spinner"></span>{/if} Créer
        </button>
        <button class="ghost" onclick={onclose}>Annuler</button>
      </div>
    {/if}
  </div>
</div>
